import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import { Calendar, User, Tag, Search, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getBlogPageData, formatBlogPageData } from '../api/strapi/blogPageApi';
import { getArticles, formatArticles } from '../api/strapi/articlesApi';
import SectionGuard from '../components/SectionGuard';
import { useLanguage } from '../context';
import { getLocalizedPath } from '../helpers/routes';
import DataLoader from '../components/loader/DataLoader';
import PageHero from '../components/common/PageHero';
import SectionHeader from '../components/common/SectionHeader';
import SkeletonImage from '../components/common/SkeletonImage';

const BlogPage = () => {
  const intl = useIntl();
  const [blogs, setBlogs] = useState([]);
  const [featuredBlog, setFeaturedBlog] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [pageResult, setPageResult] = useState(null);
  const [articlesResult, setArticlesResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { language } = useLanguage();

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch page data and articles separately to handle potential errors independently
        const [blogPageResult, articlesFetchResult] = await Promise.all([
          getBlogPageData(language),
          getArticles(language),
        ]);

        setPageResult(blogPageResult);
        setArticlesResult(articlesFetchResult);

        if (!blogPageResult.ok && blogPageResult.reason !== "NOT_FOUND") {
          console.warn("Blog page data unavailable:", blogPageResult);
        }
        if (!articlesFetchResult.ok && articlesFetchResult.reason !== "NOT_FOUND") {
          console.warn("Blog articles unavailable:", articlesFetchResult);
        }

        const formattedPageData = blogPageResult.ok
          ? formatBlogPageData(blogPageResult.data)
          : null;
        const formattedArticles = articlesFetchResult.ok && Array.isArray(articlesFetchResult.data)
          ? formatArticles(articlesFetchResult.data)
          : [];

        setBlogs(formattedArticles);
        setFeaturedBlog(formattedArticles.length > 0 ? formattedArticles[0] : null);
        setPageData(formattedPageData);
      } catch (error) {
        console.error('Error fetching blog page data:', error);

        // Set empty arrays if API fails
        setBlogs([]);
        setFeaturedBlog(null);
        setPageData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  // Generate categories dynamically from blog data, only if blog data exists
  const allCategories = blogs && Array.isArray(blogs) ? [...new Set(blogs.map(item => item.category).filter(Boolean))] : [];
  const categories = [
    { name: 'all', label: intl.formatMessage({ id: "blog.categories.all" }), count: blogs && Array.isArray(blogs) ? blogs.length : 0 },
    ...allCategories.map(category => ({
      name: category,
      label: category,
      count: (blogs && Array.isArray(blogs)) ? blogs.filter(n => n.category === category).length : 0
    }))
  ];

  const filteredBlogs = (blogs && Array.isArray(blogs) ? blogs : []).filter(blog => {
    const matchesCategory = selectedCategory === 'all' || blog.category === selectedCategory;
    const matchesSearch = blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch && featuredBlog && blog.id !== featuredBlog.id;
  });

  // Use stats from API data only, no fallback to prevent errors
  const stats = pageData?.stats || [];

  const sectionFallback = (
    <div className="py-10 text-center text-gray-500 text-sm">
      {intl.formatMessage({ id: "empty.section" })}
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageData?.title ? `${pageData.title} | Africa Systems` : intl.formatMessage({ id: "blog.meta.title" })}</title>
        <meta name="description" content={pageData?.heroComponent?.description || ""} />
      </Helmet>

      {/* Hero Section */}
      <SectionGuard result={pageResult} fallback={sectionFallback} logLabel="Blog hero">
        {pageData?.heroComponent && (
          <PageHero
            eyebrow="Africa Systems"
            title={pageData.heroComponent.title}
            description={pageData.heroComponent.description}
            image={pageData.heroComponent.image || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&q=80'}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="w-full max-w-2xl"
            >
              <div className="relative">
                <input
                  type="text"
                  placeholder={intl.formatMessage({ id: "blog.search.placeholder" })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-6 py-4 pl-12 rounded-full text-gray-900 focus:outline-none focus:ring-4 focus:ring-[#16A34A]"
                />
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              </div>
            </motion.div>
          </PageHero>
        )}
      </SectionGuard>

      {/* Stats Section */}
      <SectionGuard result={pageResult} fallback={null} logLabel="Blog stats">
        {pageData?.stats && pageData.stats.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {pageData.stats.map((stat, index) => (
                <motion.div
                  key={stat.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="text-3xl font-bold text-[#0a1628] mb-2">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        )}
      </SectionGuard>

      {/* Featured Article - CORRECTION RESPONSIVE ICI */}
      {featuredBlog && (
        <section className="py-16">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="group bg-white border border-slate-200 hover:border-[#16A34A]/40 hover:shadow-lg transition-all duration-300 overflow-hidden"
            >
              {/* Passage en flex-col sur mobile, lg:flex-row sur desktop */}
              <div className="flex flex-col lg:flex-row">
                <div className="lg:w-1/2">
                  <SkeletonImage
                    src={featuredBlog.image}
                    alt={featuredBlog.title}
                    className="w-full h-64 lg:h-full"
                    imgClassName="w-full h-full object-cover"
                  />
                </div>
                <div className="lg:w-1/2 p-6 lg:p-12 flex flex-col justify-center">
                  <span className="inline-block bg-[#16A34A]/10 text-[#15803D] text-sm font-semibold px-3 py-1 rounded-full mb-4 w-fit">
                    {featuredBlog.category}
                  </span>
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#0a1628] group-hover:text-[#16A34A] transition-colors mb-4">{featuredBlog.title}</h2>
                  <p className="text-slate-600 mb-6">{featuredBlog.excerpt}</p>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center">
                      <SkeletonImage
                        src={featuredBlog.author.avatar}
                        alt={featuredBlog.author.name}
                        className="w-10 h-10 rounded-full mr-3"
                        imgClassName="w-full h-full rounded-full object-cover"
                        icon={false}
                      />
                      <div>
                        <div className="font-semibold text-[#0a1628]">{featuredBlog.author.name}</div>
                        <div className="flex items-center text-sm text-slate-500">
                          <Calendar className="w-4 h-4 mr-1" />
                          {featuredBlog.date} • {featuredBlog.readTime}
                        </div>
                      </div>
                    </div>
                    <Link
                      to={getLocalizedPath(`/blog/${featuredBlog.slug}`, language)}
                      className="bg-[#16A34A] hover:bg-[#15803D] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 text-center"
                    >
                      {intl.formatMessage({ id: "blog.readMore" })}
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Category Filter - CORRECTION RESPONSIVE ICI */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
            <Filter className="w-5 h-5 text-gray-600 hidden sm:block" />
            {categories.map((category) => (
              <button
                key={category.name}
                onClick={() => setSelectedCategory(category.name)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  selectedCategory === category.name
                    ? 'bg-[#16A34A] text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category.label} ({category.count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid - CORRECTION RESPONSIVE ICI */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <SectionHeader
            eyebrow="Africa Systems"
            title={intl.formatMessage({ id: "blog.latestArticles" })}
            align="center"
          />

          {loading ? (
            <DataLoader isLoading={true} />
          ) : (
            // Passage en 2 colonnes sur tablette (md:grid-cols-2) et 3 sur desktop
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredBlogs.map((blog, index) => (
                <Link
                  to={getLocalizedPath(`/blog/${blog.slug}`, language)}
                  key={`link-${blog.id}`}
                  className="block"
                >
                  <motion.article
                    key={blog.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="group bg-white border border-slate-200 hover:border-[#16A34A]/40 hover:shadow-lg transition-all duration-300 overflow-hidden h-full flex flex-col"
                  >
                    <SkeletonImage
                      src={blog.image}
                      alt={blog.title}
                      className="w-full h-48 flex-shrink-0"
                      imgClassName="w-full h-full object-cover"
                    />
                    <div className="p-6 flex flex-col flex-grow">
                      <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
                        <span className="inline-block bg-[#16A34A]/10 text-[#15803D] text-xs font-semibold px-2 py-1 rounded-full truncate max-w-[60%]">
                          {blog.category}
                        </span>
                        <div className="flex items-center text-sm text-slate-500 whitespace-nowrap">
                          <Calendar className="w-4 h-4 mr-1" />
                          {blog.date}
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-[#0a1628] group-hover:text-[#16A34A] transition-colors mb-3 line-clamp-2">{blog.title}</h3>
                      <p className="text-slate-600 mb-4 line-clamp-3">{blog.excerpt}</p>
                      <div className="flex items-center justify-between mt-auto pt-2">
                        <div className="flex items-center min-w-0">
                          <SkeletonImage
                            src={blog.author.avatar}
                            alt={blog.author.name}
                            className="w-8 h-8 rounded-full mr-2 flex-shrink-0"
                            imgClassName="w-full h-full rounded-full object-cover"
                            icon={false}
                          />
                          <span className="text-sm font-medium text-[#0a1628] truncate">{blog.author.name}</span>
                        </div>
                        <span className="text-sm text-slate-500 flex-shrink-0 ml-2">{blog.readTime}</span>
                      </div>
                      <span className="inline-flex items-center gap-2 text-[#16A34A] font-semibold text-sm mt-4">
                        {intl.formatMessage({ id: "blog.readMore" })}
                      </span>
                    </div>
                  </motion.article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA - CORRECTION RESPONSIVE ICI */}
      <section className="py-20 bg-[#0a1628]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {intl.formatMessage({ id: "blog.newsletter.title" })}
            </h2>
            <p className="text-xl text-white/75 max-w-2xl mx-auto mb-8 px-4">
              {intl.formatMessage({ id: "blog.newsletter.description" })}
            </p>
            {/* Passage en flex-col sur mobile pour éviter que le bouton soit coupé */}
            <div className="max-w-md mx-auto flex flex-col sm:flex-row gap-2 sm:gap-0">
              <input
                type="email"
                placeholder={intl.formatMessage({ id: "blog.newsletter.placeholder" })}
                className="flex-1 px-4 py-3 rounded-lg sm:rounded-r-none sm:rounded-l-lg text-gray-900 focus:outline-none"
              />
              <button className="bg-[#16A34A] hover:bg-[#15803D] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] rounded-lg sm:rounded-l-none sm:rounded-r-lg transition-colors duration-200">
                {intl.formatMessage({ id: "blog.newsletter.subscribe" })}
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default BlogPage;